import React, { useState } from "react";
import {
  Sparkles,
  RefreshCw,
  Loader2,
  Wand2,
  Mic,
  Droplet,
  Trash2,
  PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useDemoMode } from "../context/DemoModeContext";
import {
  deleteStylePreferences,
  deleteFaceShape,
} from "../lib/api";
import { track } from "../lib/analytics";
import { launchDemoScript } from "./DemoScript";

/**
 * DemoControlsCard — admin-only utilities for demoing the app to
 * investors, press, friends, beta cohort. Lets the founder reset
 * various "remember-me" features back to first-run state so the
 * magic moments (Trouvé's intro, the voice tutorial, fresh
 * onboarding) replay live.
 *
 * Visibility: gated on `user.is_admin` (server-side flag from
 * `_is_admin_email(email)` against `DEV_EMAILS`). Non-admin users
 * never see this card.
 *
 * Each action is intentionally a single click — no confirmation
 * prompt — because demo flow shouldn't be interrupted by modals.
 * The actions are reversible / harmless (intros just re-play, prefs
 * regenerate on next routine).
 */
const TROUVE_INTRO_KEY = "trubeaute.trouve.intro_played.v1";
const TROUVE_TUTORIAL_KEY = "trubeaute.trouve.voice_tutorial_played.v1";
const TROUVE_VOICE_PREF_KEY = "trubeaute.trouve.voice_commands.v1";

const DemoControlsCard = () => {
  const { user } = useAuth();
  const { active: demoActive, setActive: setDemoActive } = useDemoMode();
  const [busy, setBusy] = useState(null);

  if (!user?.is_admin) return null;

  const toggleDemo = () => {
    const next = !demoActive;
    setDemoActive(next);
    track(next ? "demo_mode_enabled" : "demo_mode_disabled");
    toast.success(
      next
        ? "Demo mode on 💋 — TTS + voice forced on for this session."
        : "Demo mode off — your normal preferences are back.",
    );
  };

  const _clearLocal = (keys) => {
    try {
      keys.forEach((k) => window.localStorage.removeItem(k));
    } catch {
      /* private mode → silent */
    }
  };

  const resetIntros = () => {
    _clearLocal([TROUVE_INTRO_KEY, TROUVE_TUTORIAL_KEY, TROUVE_VOICE_PREF_KEY]);
    track("demo_reset_trouve_intros");
    toast.success("Trouvé's intro + tutorial reset. Open the mirror to replay.");
  };

  const resetOnboarding = async () => {
    setBusy("onboarding");
    try {
      await deleteStylePreferences();
      track("demo_reset_onboarding");
      toast.success("Onboarding cleared. /onboarding will run fresh next time.");
    } catch {
      toast.error("Couldn't clear. Try again in a sec.");
    } finally {
      setBusy(null);
    }
  };

  const resetFaceShape = async () => {
    setBusy("face_shape");
    try {
      await deleteFaceShape();
      track("demo_reset_face_shape");
      toast.success("Face shape cleared. Open the mirror to re-detect.");
    } catch {
      toast.error("Couldn't clear. Try again in a sec.");
    } finally {
      setBusy(null);
    }
  };

  const resetEverything = async () => {
    setBusy("everything");
    try {
      _clearLocal([TROUVE_INTRO_KEY, TROUVE_TUTORIAL_KEY, TROUVE_VOICE_PREF_KEY]);
      await Promise.all([deleteStylePreferences(), deleteFaceShape()]);
      track("demo_reset_everything");
      toast.success(
        "Demo state reset 💋 — onboarding, intros, tutorial, face shape all fresh.",
      );
    } catch {
      toast.error("Some resets failed. Check the console.");
    } finally {
      setBusy(null);
    }
  };

  const Btn = ({ onClick, disabled, busyKey, icon: Icon, label, testid }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy === busyKey}
      className="inline-flex items-center gap-2 bg-white border border-[#EADDD7] hover:border-[#9B6A6C]/40 hover:bg-[#FDF6F3] text-[#5C4B51] hover:text-[#2C2123] rounded-full px-3.5 py-2 text-xs font-medium transition-colors disabled:opacity-50"
      data-testid={testid}
    >
      {busy === busyKey ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Icon className="h-3.5 w-3.5" />
      )}
      {label}
    </button>
  );

  return (
    <div
      className="bg-gradient-to-br from-[#1F2329] to-[#2C2123] border border-[#9B6A6C]/30 rounded-3xl p-7 mb-5 text-[#FAF6F2]"
      data-testid="demo-controls-card"
    >
      <div className="flex items-start gap-4 mb-5">
        <div className="shrink-0 h-11 w-11 rounded-full bg-[#9B6A6C]/25 border border-[#9B6A6C]/50 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-[#FFE6D7]" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-xl font-medium leading-tight">Demo controls</h3>
            <span className="text-[10px] uppercase tracking-[0.18em] bg-[#9B6A6C]/30 border border-[#9B6A6C]/40 rounded-full px-2 py-0.5 text-[#FFE6D7]">
              Founder
            </span>
          </div>
          <p className="text-sm text-white/70 max-w-xl leading-relaxed">
            Reset the moments that only happen once — Trouvé's intro, the
            voice tutorial, fresh onboarding — so investors and press see the
            full magic when you demo.
          </p>
        </div>
      </div>

      {/* ── Demo mode toggle ────────────────────────────────────
          Forces TTS + voice commands on for the duration of this
          tab/session. Persists in sessionStorage so a tab refresh
          doesn't drop demo posture mid-pitch. */}
      <div
        className={`flex items-center gap-3 p-4 rounded-2xl mb-4 border transition-colors ${
          demoActive
            ? "bg-[#9B6A6C]/25 border-[#D5A8A4]/60"
            : "bg-white/[0.04] border-white/10"
        }`}
        data-testid="demo-mode-toggle-row"
      >
        <div
          className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center transition-colors ${
            demoActive
              ? "bg-[#FFE6D7] text-[#5D4050]"
              : "bg-white/10 text-white/60"
          }`}
        >
          <PlayCircle className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Demo mode</span>
            {demoActive && (
              <span className="text-[10px] uppercase tracking-[0.16em] bg-[#FFE6D7] text-[#5D4050] rounded-full px-2 py-0.5">
                LIVE
              </span>
            )}
          </div>
          <p className="text-xs text-white/55 leading-snug mt-0.5">
            Forces TTS + voice commands on for this session so investors
            see the full magic. Auto-clears when you close the tab.
          </p>
        </div>
        <button
          type="button"
          onClick={toggleDemo}
          role="switch"
          aria-checked={demoActive}
          className={`relative shrink-0 h-7 w-12 rounded-full transition-colors ${
            demoActive ? "bg-[#FFE6D7]" : "bg-white/15"
          }`}
          data-testid="demo-mode-toggle"
        >
          <span
            className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${
              demoActive ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        <Btn
          onClick={resetIntros}
          icon={Mic}
          label="Replay Trouvé's intros"
          testid="demo-reset-intros"
          busyKey="intros"
        />
        <Btn
          onClick={resetOnboarding}
          icon={Wand2}
          label="Restart onboarding"
          testid="demo-reset-onboarding"
          busyKey="onboarding"
        />
        <Btn
          onClick={resetFaceShape}
          icon={Droplet}
          label="Re-detect face shape"
          testid="demo-reset-face-shape"
          busyKey="face_shape"
        />
        <Btn
          onClick={resetEverything}
          icon={RefreshCw}
          label="Reset everything"
          testid="demo-reset-everything"
          busyKey="everything"
        />
      </div>

      {/* Scripted demo launcher — only visible when demo mode is on,
          since the overlay itself is gated on demoActive too. Drops
          any current Routine page into a 5-beat narrated tour. */}
      {demoActive && (
        <button
          type="button"
          onClick={() => {
            launchDemoScript();
            track("demo_script_launched_from_settings");
            toast.success(
              "Demo tour queued. Open any routine to see Trouvé walk through.",
            );
          }}
          className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FFE6D7] to-[#D5A8A4] hover:from-white hover:to-[#FFE6D7] text-[#5D4050] rounded-full px-4 py-2.5 text-sm font-medium transition-colors mb-3"
          data-testid="demo-script-launch"
        >
          <PlayCircle className="h-4 w-4" />
          Start scripted demo on next routine
        </button>
      )}

      <p className="text-[11px] text-white/45 leading-relaxed flex items-start gap-1.5">
        <Trash2 className="h-3 w-3 mt-0.5 shrink-0" />
        Each reset is reversible — the saved state regenerates on the next
        routine. Nothing here affects your account or billing.
      </p>
    </div>
  );
};

export default DemoControlsCard;
